<template>
  <v-card flat class="pa-4">
    <div class="rd-head">
      <span class="rd-title">Roluri &amp; drepturi</span>
      <div class="rd-company">
        <DxSelectBox
          :data-source="companii"
          value-expr="ID"
          display-expr="Name"
          placeholder="Selectați firma..."
          width="280"
          :value="selectedCompanyId"
          @value-changed="onCompanyChanged"
        />
      </div>
    </div>
    <v-divider class="my-3"></v-divider>

    <div v-if="!selectedCompanyId" class="rd-empty">
      Selectați o firmă pentru a gestiona rolurile și drepturile.
    </div>

    <div v-else class="rd-body">
      <!-- STÂNGA: roluri -->
      <div class="rd-col rd-col-roluri">
        <div class="rd-col-head">
          <span>Roluri</span>
          <DxButton icon="add" text="Rol nou" styling-mode="text" @click="openAddRol" />
        </div>
        <DxDataGrid
          :data-source="roluri"
          key-expr="ID"
          :show-borders="true"
          :show-column-lines="false"
          :show-row-lines="true"
          :row-alternation-enabled="true"
          :hover-state-enabled="true"
          :column-auto-width="true"
          height="calc(100vh - 320px)"
          :selected-row-keys="selectedRolKeys"
          @selection-changed="onSelectionChanged"
        >
          <DxSelection mode="single" />
          <DxSearchPanel :visible="true" placeholder="Caută rol..." />
          <DxPaging :page-size="20" />
          <DxColumn data-field="Cod" caption="Cod" :width="120" />
          <DxColumn data-field="Nume" caption="Nume" />
          <DxColumn data-field="Explicatie" caption="Explicație" />
          <DxColumn caption="" :width="90" cell-template="rolActions" />

          <template #rolActions="{ data }">
            <div class="row-actions">
              <DxButton icon="edit" styling-mode="text" @click="openEditRol(data.data)" />
              <DxButton icon="trash" styling-mode="text" @click="confirmDeleteRol(data.data)" />
            </div>
          </template>
        </DxDataGrid>
      </div>

      <!-- DREAPTA: drepturi / utilizatori rol selectat -->
      <div class="rd-col rd-col-drepturi">
        <div class="rd-col-head">
          <div class="rd-tabs">
            <button
              type="button"
              class="rd-tab-btn"
              :class="{ 'rd-tab-active': dreapraTab === 'drepturi' }"
              @click="dreapraTab = 'drepturi'"
            >Drepturi</button>
            <button
              type="button"
              class="rd-tab-btn"
              :class="{ 'rd-tab-active': dreapraTab === 'utilizatori' }"
              @click="dreapraTab = 'utilizatori'"
            >Utilizatori</button>
          </div>
          <DxButton
            v-if="rolSelectat && dreapraTab === 'drepturi'"
            text="Salvează drepturile"
            styling-mode="contained"
            type="default"
            :disabled="!hasChanges"
            @click="saveDrepturi"
          />
        </div>

        <div v-if="!rolSelectat" class="rd-empty rd-empty-inline">
          Selectați un rol din stânga pentru a-i vedea/edita drepturile.
        </div>

        <DxDataGrid
          v-else-if="dreapraTab === 'drepturi'"
          :data-source="drepturiRol"
          key-expr="ID"
          :show-borders="true"
          :show-column-lines="false"
          :show-row-lines="true"
          :row-alternation-enabled="true"
          :hover-state-enabled="true"
          :column-auto-width="true"
          height="calc(100vh - 320px)"
        >
          <DxSearchPanel :visible="true" placeholder="Caută drept..." />
          <DxPaging :page-size="50" />
          <DxColumn data-field="Nume" caption="Denumire" />
          <DxColumn data-field="Cod" caption="Cod" :width="180" />
          <DxColumn caption="Alocat" :width="90" alignment="center" cell-template="alocatCell" />

          <template #alocatCell="{ data }">
            <input
              type="checkbox"
              :checked="data.data.HasRight"
              @change="toggleDrept(data.data, ($event.target as HTMLInputElement).checked)"
            />
          </template>
        </DxDataGrid>

        <DxDataGrid
          v-else
          :data-source="utilizatoriFirma"
          key-expr="ID"
          :show-borders="true"
          :show-column-lines="false"
          :show-row-lines="true"
          :row-alternation-enabled="true"
          :hover-state-enabled="true"
          :column-auto-width="true"
          height="calc(100vh - 320px)"
        >
          <DxSearchPanel :visible="true" placeholder="Caută utilizator..." />
          <DxPaging :page-size="50" />
          <DxColumn data-field="Username" caption="Utilizator" />
          <DxColumn data-field="Email" caption="Email" />
          <DxColumn caption="Alocat" :width="90" alignment="center" cell-template="alocatUserCell" />

          <template #alocatUserCell="{ data }">
            <input
              type="checkbox"
              :checked="isUserAlocat(data.data.ID)"
              @change="toggleUtilizator(data.data, ($event.target as HTMLInputElement).checked)"
            />
          </template>
        </DxDataGrid>
      </div>
    </div>

    <!-- Popup Add/Edit rol -->
    <DxPopup
      v-model:visible="rolPopupVisible"
      :title="rolEditMode ? 'Editare rol' : 'Rol nou'"
      :width="440"
      height="auto"
      :drag-enabled="false"
      :wrapper-attr="{ class: 'era-edit-popup' }"
    >
      <div class="rol-form">
        <label>Cod</label>
        <DxTextBox v-model:value="rolForm.Cod" placeholder="Cod rol" />
        <label>Nume</label>
        <DxTextBox v-model:value="rolForm.Nume" placeholder="Nume rol" />
        <label>Explicație</label>
        <DxTextArea v-model:value="rolForm.Explicatie" placeholder="Explicație (opțional)" :height="80" />
        <div class="ff-actions">
          <span class="ff-spacer"></span>
          <DxButton text="Salvează" type="default" styling-mode="contained" @click="saveRol" />
          <DxButton text="Anulează" styling-mode="outlined" @click="rolPopupVisible = false" />
        </div>
      </div>
    </DxPopup>

    <!-- Popup confirmare delete rol -->
    <DxPopup
      v-model:visible="deleteRolVisible"
      title="Ștergere rol"
      :width="420"
      height="auto"
      :drag-enabled="false"
      :wrapper-attr="{ class: 'era-edit-popup' }"
    >
      <div class="del-body">
        <p>Sigur vrei să ștergi rolul <strong>„{{ toDeleteRol && toDeleteRol.Nume }}"</strong>?</p>
        <p class="del-warn">Toate drepturile asociate acestui rol vor deveni indisponibile.</p>
        <div class="ff-actions">
          <span class="ff-spacer"></span>
          <DxButton text="Șterge" type="danger" styling-mode="contained" @click="doDeleteRol" />
          <DxButton text="Anulează" styling-mode="outlined" @click="deleteRolVisible = false" />
        </div>
      </div>
    </DxPopup>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import DxDataGrid, { DxColumn, DxPaging, DxSearchPanel, DxSelection } from "devextreme-vue/data-grid";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxPopup } from "devextreme-vue/popup";
import { DxButton } from "devextreme-vue/button";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxTextArea } from "devextreme-vue/text-area";
import notify from "devextreme/ui/notify";
import RoluriDrepturiService from "@/services/AUTH/RoluriDrepturi";
import ADServices from "@/services/AUTH/AD_Services";

const TIP_DREPT = "AUTH_DrepturiCustom";

export default defineComponent({
  name: "RoluriDrepturiGrid",
  components: {
    DxDataGrid, DxColumn, DxPaging, DxSearchPanel, DxSelection,
    DxSelectBox, DxPopup, DxButton, DxTextBox, DxTextArea,
  },
  setup() {
    const companii = ref<any[]>([]);
    const selectedCompanyId = ref<string | null>(null);

    const roluri = ref<any[]>([]);
    const rolSelectat = ref<any>(null);
    const selectedRolKeys = ref<any[]>([]);

    const drepturiRol = ref<any[]>([]);
    const drepturiInitiale = ref<Map<string, boolean>>(new Map());
    const hasChanges = ref(false);

    const dreapraTab = ref<"drepturi" | "utilizatori">("drepturi");
    const utilizatoriFirma = ref<any[]>([]);
    const utilizatoriRolAlocati = ref<Set<string>>(new Set());

    const rolPopupVisible = ref(false);
    const rolEditMode = ref(false);
    const rolForm = ref<{ ID?: string; Cod: string; Nume: string; Explicatie: string }>({ Cod: "", Nume: "", Explicatie: "" });

    const deleteRolVisible = ref(false);
    const toDeleteRol = ref<any>(null);

    const loadCompanii = async () => {
      try {
        const res = await ADServices.getCompanies();
        companii.value = res.data;
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la încărcarea firmelor.", "error", 4000);
      }
    };

    const loadRoluri = async () => {
      try {
        const res = await RoluriDrepturiService.getRoluri();
        roluri.value = res.data;
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la încărcarea rolurilor.", "error", 4000);
      }
    };

    const onCompanyChanged = async (e: any) => {
      const companyId = e.value;
      selectedCompanyId.value = companyId;
      rolSelectat.value = null;
      selectedRolKeys.value = [];
      drepturiRol.value = [];
      utilizatoriFirma.value = [];
      utilizatoriRolAlocati.value = new Set();
      if (!companyId) return;
      try {
        await RoluriDrepturiService.switchCompany(companyId);
        await loadRoluri();
        await loadUtilizatoriFirma();
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la schimbarea firmei.", "error", 4000);
      }
    };

    const loadDrepturiRol = async (rolId: string) => {
      try {
        const res = await RoluriDrepturiService.getMeniuriRol(rolId);
        drepturiRol.value = res.data;
        drepturiInitiale.value = new Map(res.data.map((d: any) => [d.ID, !!d.HasRight]));
        hasChanges.value = false;
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la încărcarea drepturilor.", "error", 4000);
      }
    };

    const loadUtilizatoriFirma = async () => {
      try {
        const res = await RoluriDrepturiService.getCompanyUsers();
        utilizatoriFirma.value = res.data;
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la încărcarea utilizatorilor firmei.", "error", 4000);
      }
    };

    const loadUtilizatoriRol = async (rolId: string) => {
      try {
        const res = await RoluriDrepturiService.getRoleUsers(rolId);
        utilizatoriRolAlocati.value = new Set((res.data || []).map((u: any) => u.UserID));
      } catch (e: any) {
        if (e?.response?.status === 404) {
          utilizatoriRolAlocati.value = new Set();
        } else {
          notify(e?.response?.data?.Message || "Eroare la încărcarea utilizatorilor rolului.", "error", 4000);
        }
      }
    };

    const isUserAlocat = (userId: string) => utilizatoriRolAlocati.value.has(userId);

    const toggleUtilizator = async (row: any, checked: boolean) => {
      if (!rolSelectat.value) return;
      const rolId = rolSelectat.value.ID;
      try {
        if (checked) {
          await RoluriDrepturiService.addRoleUser(row.ID, rolId);
          notify("Utilizator alocat.", "success", 2500);
        } else {
          await RoluriDrepturiService.removeRoleUser(row.ID, rolId);
          notify("Utilizator revocat.", "success", 2500);
        }
        await loadUtilizatoriRol(rolId);
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la actualizarea alocării.", "error", 4000);
      }
    };

    const onSelectionChanged = (e: any) => {
      const row = e.selectedRowsData && e.selectedRowsData[0];
      rolSelectat.value = row || null;
      selectedRolKeys.value = row ? [row.ID] : [];
      if (row) {
        loadDrepturiRol(row.ID);
        loadUtilizatoriRol(row.ID);
      }
    };

    const toggleDrept = (row: any, checked: boolean) => {
      row.HasRight = checked;
      const initial = drepturiInitiale.value.get(row.ID) || false;
      hasChanges.value = drepturiRol.value.some(d => d.HasRight !== (drepturiInitiale.value.get(d.ID) || false));
    };

    const saveDrepturi = async () => {
      if (!rolSelectat.value) return;
      const rolId = rolSelectat.value.ID;
      try {
        for (const d of drepturiRol.value) {
          const initial = drepturiInitiale.value.get(d.ID) || false;
          if (d.HasRight && !initial) {
            await RoluriDrepturiService.addDreptRol({
              RolId: rolId, AgentId: d.ID, Citeste: true, Modifica: true, Sterge: true, Tip: TIP_DREPT,
            });
          } else if (!d.HasRight && initial) {
            await RoluriDrepturiService.deleteDreptRol({
              RolId: rolId, AgentId: d.ID, Tip: TIP_DREPT, RightId: d.RightId,
            });
          }
        }
        notify("Drepturi actualizate.", "success", 2500);
        await loadDrepturiRol(rolId);
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la salvarea drepturilor.", "error", 4000);
      }
    };

    const openAddRol = () => {
      rolEditMode.value = false;
      rolForm.value = { Cod: "", Nume: "", Explicatie: "" };
      rolPopupVisible.value = true;
    };

    const openEditRol = (row: any) => {
      rolEditMode.value = true;
      rolForm.value = { ID: row.ID, Cod: row.Cod, Nume: row.Nume, Explicatie: row.Explicatie || "" };
      rolPopupVisible.value = true;
    };

    const saveRol = async () => {
      if (!rolForm.value.Cod || !rolForm.value.Nume) {
        notify("Cod și Nume sunt obligatorii.", "warning", 3000);
        return;
      }
      try {
        if (rolEditMode.value) {
          await RoluriDrepturiService.updateRol({
            ID: rolForm.value.ID!, Cod: rolForm.value.Cod, Nume: rolForm.value.Nume, Explicatie: rolForm.value.Explicatie,
          });
          notify("Rol actualizat.", "success", 2500);
        } else {
          await RoluriDrepturiService.addRol({
            Cod: rolForm.value.Cod, Nume: rolForm.value.Nume, Explicatie: rolForm.value.Explicatie,
          });
          notify("Rol adăugat.", "success", 2500);
        }
        rolPopupVisible.value = false;
        await loadRoluri();
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la salvarea rolului.", "error", 4000);
      }
    };

    const confirmDeleteRol = (row: any) => {
      toDeleteRol.value = row;
      deleteRolVisible.value = true;
    };

    const doDeleteRol = async () => {
      if (!toDeleteRol.value) return;
      try {
        await RoluriDrepturiService.deleteRol(toDeleteRol.value.ID);
        notify("Rol șters.", "success", 2500);
        deleteRolVisible.value = false;
        if (rolSelectat.value && rolSelectat.value.ID === toDeleteRol.value.ID) {
          rolSelectat.value = null;
          selectedRolKeys.value = [];
          drepturiRol.value = [];
        }
        await loadRoluri();
      } catch (e: any) {
        notify(e?.response?.data?.Message || "Eroare la ștergerea rolului.", "error", 4000);
      }
    };

    onMounted(loadCompanii);

    return {
      companii, selectedCompanyId, onCompanyChanged,
      roluri, rolSelectat, selectedRolKeys, onSelectionChanged,
      drepturiRol, hasChanges, toggleDrept, saveDrepturi,
      dreapraTab, utilizatoriFirma, isUserAlocat, toggleUtilizator,
      rolPopupVisible, rolEditMode, rolForm, openAddRol, openEditRol, saveRol,
      deleteRolVisible, toDeleteRol, confirmDeleteRol, doDeleteRol,
    };
  },
});
</script>

<style scoped>
.rd-head { display: flex; align-items: center; justify-content: space-between; }
.rd-title { font-size: 1.1rem; font-weight: 600; color: #1e293b; }

.rd-empty {
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}
.rd-empty-inline { padding: 30px 10px; }

.rd-body { display: flex; gap: 18px; align-items: flex-start; }
.rd-col { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.rd-col-roluri { flex: 1; }
.rd-col-drepturi { flex: 1.1; }

.rd-col-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.row-actions { display: flex; gap: 2px; }

.rd-tabs { display: flex; gap: 4px; }
.rd-tab-btn {
  border: none;
  background: transparent;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  cursor: pointer;
}
.rd-tab-btn:hover { background: #f1f5f9; color: #334155; }
.rd-tab-active { background: #ff2c21; color: #fff; }
.rd-tab-active:hover { background: #ff2c21; color: #fff; }

.rol-form { display: flex; flex-direction: column; gap: 4px; padding: 4px 2px; }
.rol-form label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.5px; margin-top: 6px; }

.ff-actions { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
.ff-spacer { flex: 1; }

.del-body { display: flex; flex-direction: column; gap: 8px; padding: 6px 2px; font-size: 14px; color: #334155; }
.del-warn { color: #94a3b8; font-size: 12.5px; }
</style>
